import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons/faCircleCheck';
import type { ColDef } from 'ag-grid-community';
import { ROUTES } from 'shared/constants';
import { formatNumber } from 'shared/utils';
import { useDateFormatter } from 'shared/hooks/dates.hooks';
import { useUnmatchedProviders } from 'shared/hooks/quality-metric.hooks';
import type { UnmatchedProvider } from 'shared/api/quality-metric-rq.service.api';
import { fetchUsers } from 'shared/store/actions/users-management.actions';
import { Button } from 'src/components/design-system';
import { Modal } from 'src/components/design-system/modal';
import { Table, cellActionKeyboardNavigate } from 'src/components/design-system/table';
import { useDispatch } from 'src/store/use-dispatch';
import { Loader } from 'src/components/core';
import { useQualityMetricsManagementContext } from '../quality-metrics-management.context';
import { resolveMetricTitles } from '../quality-metrics-management.utils';
import { getProviderLabel } from 'src/components/user-details/provider-identification';
import { AssignProviderModal, type OrgUser } from './assign-provider-modal';
import styles from '../quality-metrics-management.module.scss';

const GRID_PAGE_SIZE = 20;
const GRID_PAGE_SIZE_OPTIONS = [20, 50, 100];

interface UnmatchedProvidersModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Org-wide list of the provider names on records no user is identified with.
 * A row is one name under one provider (the same name can be listed under
 * two). Each row can be assigned to a user from here (or from Users
 * management → provider names); the list refreshes when a user save sets a name.
 *
 * @param props.open - Whether the modal shows.
 * @param props.onClose - Called on dismiss.
 * @returns The modal.
 */
export function UnmatchedProvidersModal({ open, onClose }: UnmatchedProvidersModalProps) {
  const { lang, metrics } = useQualityMetricsManagementContext();
  const { formatLongDate } = useDateFormatter();
  const { data: providers = [], isLoading, error } = useUnmatchedProviders();
  const dispatch = useDispatch();
  const users = useSelector(
    ({ usersManagement }: { usersManagement: { list: OrgUser[] | null } }) => usersManagement.list,
  );
  const [assigning, setAssigning] = useState<UnmatchedProvider | null>(null);

  // The org's users are only needed once someone picks a row to assign.
  const openAssign = useCallback(
    (row: UnmatchedProvider) => {
      if (users === null) dispatch(fetchUsers());
      setAssigning(row);
    },
    [users, dispatch],
  );

  const columns = useMemo(
    (): ColDef<UnmatchedProvider>[] => [
      {
        field: 'provider',
        headerName: lang.COLUMN_PROVIDER,
        flex: 1,
        filter: true,
        valueFormatter: ({ value }) => (value ? getProviderLabel(value) : ''),
      },
      {
        field: 'externalUserName',
        headerName: lang.COLUMN_PROVIDER_NAME,
        flex: 2,
        filter: true,
      },
      {
        field: 'recordsCount',
        headerName: lang.COLUMN_RECORDS,
        flex: 1,
        valueFormatter: ({ value }) => formatNumber(value ?? 0),
      },
      {
        colId: 'metrics',
        headerName: lang.COLUMN_METRICS,
        flex: 3,
        valueGetter: ({ data: row }) => row?.metricIds.length ?? 0,
        valueFormatter: ({ data: row, value }) => {
          const count = formatNumber(value ?? 0);
          const titles = row ? resolveMetricTitles(row.metricIds, metrics) : [];
          return titles.length
            ? (lang.METRICS_COUNT_WITH_TITLES ?? '')
                .replace('{count}', count)
                .replace('{titles}', titles.join(', '))
            : count;
        },
        tooltipValueGetter: ({ valueFormatted }) => valueFormatted ?? undefined,
      },
      {
        field: 'lastDate',
        headerName: lang.COLUMN_LAST_DATE,
        flex: 1,
        valueFormatter: ({ value }) => (value ? formatLongDate(value) : ''),
      },
      {
        colId: 'actions',
        headerName: lang.COLUMN_ACTIONS,
        flex: 1,
        sortable: false,
        cellRenderer: ({ data: row }: { data?: UnmatchedProvider }) =>
          row ? (
            <Button
              type="link"
              aria-label={(lang.ASSIGN_PROVIDER_LABEL ?? '').replace(
                '{name}',
                row.externalUserName,
              )}
              onClick={() => openAssign(row)}>
              {lang.ASSIGN_PROVIDER}
            </Button>
          ) : null,
        suppressKeyboardEvent: cellActionKeyboardNavigate,
      },
    ],
    [lang, metrics, formatLongDate, openAssign],
  );

  const renderBody = () => {
    if (isLoading) {
      return <Loader />;
    }
    if (error) {
      return <p className={styles.emptyText}>{lang.ERROR_GENERIC}</p>;
    }
    if (!providers.length) {
      return (
        <div className={styles.emptyState}>
          <FontAwesomeIcon icon={faCircleCheck} className={styles.emptyStateIcon} aria-hidden />
          <h3 className={styles.emptyStateTitle}>{lang.UNMATCHED_PROVIDERS_EMPTY_TITLE}</h3>
          <p className={styles.emptyStateText}>{lang.UNMATCHED_PROVIDERS_EMPTY}</p>
        </div>
      );
    }
    return (
      <Table
        containerProps={{ className: styles.usersTable }}
        columnDefs={columns}
        rowData={providers}
        getRowId={(params: { data: UnmatchedProvider }) =>
          `${params.data.provider}:${params.data.key}`
        }
        domLayout="autoHeight"
        pagination
        paginationPageSize={GRID_PAGE_SIZE}
        paginationPageSizeSelector={GRID_PAGE_SIZE_OPTIONS}
      />
    );
  };

  return (
    <Modal
      title={lang.UNMATCHED_PROVIDERS}
      open={open}
      onCancel={onClose}
      footer={null}
      width={1000}>
      <p className={styles.muted}>
        {lang.UNMATCHED_PROVIDERS_HINT}{' '}
        <Link to={ROUTES.USERS_MANAGEMENT}>{lang.UNMATCHED_PROVIDERS_USERS_LINK}</Link>
      </p>
      {renderBody()}
      <AssignProviderModal provider={assigning} users={users} onClose={() => setAssigning(null)} />
    </Modal>
  );
}
